// src/pages/BusinessPage.js
import React, { useEffect, useState, useRef } from "react";
import LoadingIndicator from "../components/LoadingIndicator";
import { collection, doc, onSnapshot, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";

// Rollöversättningar från engelska till svenska
const ROLE_LABELS_SV = {
  admin: "Administratör",
  manager: "Chef",
  user: "Användare",
  "super-admin": "Super-admin",
};

// Hjälpfunktion för att få användarens visningsnamn
function getUserDisplayName(data = {}, profile = null, currentUser = null) {
  if (data.firstName && data.lastName) {
    return `${data.firstName} ${data.lastName}`.trim();
  }

  if (data.firstName) {
    return data.firstName;
  }

  if (data.lastName) {
    return data.lastName;
  }

  if (data.name && data.name !== "Namn saknas") {
    return data.name;
  }

  if (data.displayName) {
    return data.displayName;
  }

  if (profile?.firstName && profile?.lastName) {
    return `${profile.firstName} ${profile.lastName}`.trim();
  }

  if (profile?.firstName) {
    return profile.firstName;
  }

  if (profile?.lastName) {
    return profile.lastName;
  }

  if (profile?.name) {
    return profile.name;
  }

  if (currentUser?.displayName) {
    return currentUser.displayName;
  }

  if (data.email) {
    return data.email;
  }

  if (currentUser?.email) {
    return currentUser.email;
  }

  return "Namn saknas";
}

// Huvudkomponent för företagsöversikt
function BusinessPage() {
  const { currentUser, profile } = useAuth();
  const { activeCompany, loading: companyLoading, timestampEnabled, timestampLoading, canAccessPage } = useCompany();

  const [userData, setUserData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [companyCodeError, setCompanyCodeError] = useState("");
  const [schemas, setSchemas] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [superAdminName, setSuperAdminName] = useState("Laddar...");
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingSchemas, setLoadingSchemas] = useState(true);
  const [loadingAdmins, setLoadingAdmins] = useState(true);

  const companyAccountUid = activeCompany?.companyAccountUid;
  const unsubscribeRef = useRef(null);

  // Hämta användardata och företagsdata
  useEffect(() => {
    if (!companyAccountUid) {
      setUserData(null);
      setCompanyData(null);
      setLoadingUser(false);
      return;
    }

    setLoadingUser(true);
    setCompanyCodeError("");

    const userRef = doc(db, "users", companyAccountUid);

    const unsubscribe = onSnapshot(
      userRef,
      async (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const companyId = data.companyId || activeCompany?.companyId;

          if (companyId) {
            const companyRef = doc(db, "companies", companyId);
            try {
              const companySnap = await getDoc(companyRef);
              const fetchedCompany = companySnap.exists() ? companySnap.data() : null;
              if (fetchedCompany && !fetchedCompany.companyCode) {
                try {
                  const ensureCode = httpsCallable(getFunctions(), "ensureCompanyCode");
                  const result = await ensureCode({ companyAccountUid });
                  fetchedCompany.companyCode = result.data.companyCode;
                  setCompanyCodeError("");
                } catch (error) {
                  console.error("Kunde inte tilldela företagskod:", error);
                  setCompanyCodeError("Kunde inte hämta företagskoden. Försök ladda om sidan.");
                }
              }
              setCompanyData(fetchedCompany);
            } catch (error) {
              console.error("Fel vid hämtning av företagsdata:", error);
              setCompanyData(null);
            }
          } else {
            setCompanyData(null);
          }

          // Hämta super-admin namn från employed istället för huvudkontot
          // Vi gör detta i admins useEffect istället

          setUserData({
            id: docSnapshot.id,
            companyId: companyId || activeCompany?.companyId || "company-name",
            createdAt: data.createdAt || null,
            role: data.role || activeCompany?.role || "user",
            email: data.email || currentUser?.email || "",
            name: getUserDisplayName(data, profile, currentUser),
            firstName: data.firstName || "",
            lastName: data.lastName || "",
          });
        } else {
          setUserData({
            id: companyAccountUid,
            companyId: activeCompany?.companyId || "company-name",
            createdAt: null,
            role: activeCompany?.role || "user",
            email: currentUser?.email || "",
            name: activeCompany?.companyName || getUserDisplayName({}, profile, currentUser),
            firstName: profile?.firstName || "",
            lastName: profile?.lastName || "",
          });
          setCompanyData(null);
        }

        setLoadingUser(false);
      },
      (error) => {
        console.error("Fel vid hämtning av användardata:", error);

        setUserData({
          id: companyAccountUid,
          companyId: activeCompany?.companyId || "company-name",
          createdAt: null,
          role: activeCompany?.role || "user",
          email: currentUser?.email || "",
          name: activeCompany?.companyName || getUserDisplayName({}, profile, currentUser),
          firstName: profile?.firstName || "",
          lastName: profile?.lastName || "",
        });
        setCompanyData(null);
        setLoadingUser(false);
      }
    );

    return () => unsubscribe();
  }, [companyAccountUid, activeCompany?.companyId, activeCompany?.role, activeCompany?.companyName, currentUser?.email, currentUser, profile]);

  // Hämta tidsscheman
  useEffect(() => {
    if (!companyAccountUid) {
      setSchemas([]);
      setLoadingSchemas(false);
      return;
    }

    setLoadingSchemas(true);

    const schedulingRef = collection(db, "users", companyAccountUid, "Scheduling");

    const unsubscribe = onSnapshot(
      schedulingRef,
      (snapshot) => {
        const schemaList = [];

        snapshot.forEach((schemaDoc) => {
          const data = schemaDoc.data();

          schemaList.push({
            id: schemaDoc.id,
            name: data.name || "Namnlöst schema",
          });
        });

        schemaList.sort((a, b) => (a.name || "").localeCompare(b.name || "", "sv"));
        setSchemas(schemaList);
        setLoadingSchemas(false);
      },
      (error) => {
        console.error("Fel vid hämtning av scheman:", error);
        setSchemas([]);
        setLoadingSchemas(false);
      }
    );

    return () => unsubscribe();
  }, [companyAccountUid]);

  // Hämta administratörer - ENDAST FRÅN EMPLOYED
  useEffect(() => {
    if (!companyAccountUid) {
      setAdmins([]);
      setLoadingAdmins(false);
      setSuperAdminName("Ingen super-admin hittades");
      return;
    }

    setLoadingAdmins(true);

    // Stäng av tidigare prenumeration
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const employedRef = collection(db, "users", companyAccountUid, "employed");

    const unsubscribe = onSnapshot(
      employedRef,
      (snapshot) => {
        // Använd Map med id som nyckel för att undvika dubletter
        const adminMap = new Map();
        let foundSuperAdmin = false;
        let superAdminNameTemp = "Ingen super-admin hittades";

        // HÄMTA ENDAST FRÅN EMPLOYED
        snapshot.forEach((userDoc) => {
          const data = userDoc.data();
          const role = data.role || "user";

          // Endast admin och super-admin
          if (role === "admin" || role === "super-admin") {
            const displayName = data.firstName && data.lastName 
              ? `${data.firstName} ${data.lastName}`
              : getUserDisplayName(data, null, null);
            
            // Lägg till i map med id som nyckel (unikt)
            adminMap.set(userDoc.id, {
              id: userDoc.id,
              name: displayName,
              role: role,
              firstName: data.firstName || "",
              lastName: data.lastName || "",
              email: data.email || "",
            });

            // Om det är super-admin, spara namnet
            if (role === "super-admin") {
              foundSuperAdmin = true;
              superAdminNameTemp = displayName;
            }
          }
        });

        // Uppdatera super-admin namn
        if (foundSuperAdmin) {
          setSuperAdminName(superAdminNameTemp);
        } else {
          setSuperAdminName("Ingen super-admin hittades");
        }

        // Konvertera Map till array och sortera
        const adminList = Array.from(adminMap.values());

        // Sortera: super-admin först, sedan admin, sedan alfabetiskt
        adminList.sort((a, b) => {
          if (a.role === "super-admin" && b.role !== "super-admin") return -1;
          if (b.role === "super-admin" && a.role !== "super-admin") return 1;
          return (a.name || "").localeCompare(b.name || "", "sv");
        });

        setAdmins(adminList);
        setLoadingAdmins(false);
      },
      (error) => {
        console.error("Fel vid hämtning av administratörer:", error);
        setAdmins([]);
        setLoadingAdmins(false);
        setSuperAdminName("Kunde inte hämta super-admin");
      }
    );

    unsubscribeRef.current = unsubscribe;

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [companyAccountUid]);

  // Formatera datum för visning
  const createdAtFormatted = userData?.createdAt
    ? userData.createdAt?.toDate
      ? userData.createdAt.toDate().toLocaleDateString("sv-SE")
      : new Date(userData.createdAt).toLocaleDateString("sv-SE")
    : "Okänt";

  // Visa laddningsindikator
  if (companyLoading || loadingUser || loadingSchemas || loadingAdmins) {
    return <LoadingIndicator message="Laddar företagsinformation..." fullPage />;
  }

  if (!activeCompany) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px", flexDirection: "column", gap: "16px" }}>
        <p>Inget företag valt. Vänligen logga in igen.</p>
        <button
          onClick={() => window.location.href = "/login"}
          style={{
            padding: "10px 20px",
            background: "#6323ef",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          Gå till inloggning
        </button>
      </div>
    );
  }

  const cardStyle = {
    background: "#fff",
    borderRadius: "18px",
    padding: "22px",
    border: "1px solid #ececf4",
    boxShadow: "0 8px 24px rgba(31, 41, 55, 0.04)",
  };

  const cardTitle = {
    fontSize: "18px",
    fontWeight: "700",
    marginBottom: "14px",
    color: "#2b2f42",
  };

  const listStyle = {
    paddingLeft: "18px",
    lineHeight: "1.8",
    margin: 0,
  };

  const emptyText = {
    color: "#9aa1b5",
    margin: 0,
  };

  const badgeStyle = {
    marginLeft: "8px",
    background: "#f4f1ff",
    color: "#6323ef",
    padding: "3px 8px",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "600",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "24px" }}>
      <div>
        <h1
          style={{
            fontSize: "32px",
            fontWeight: "800",
            margin: 0,
            color: "#252b42",
          }}
        >
          Företagsöversikt
        </h1>

        <p
          style={{
            color: "#8f97ad",
            marginTop: "8px",
            fontSize: "15px",
          }}
        >
          Här hittar du information om {activeCompany?.companyName}, tidsscheman och administratörer.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "20px",
        }}
      >
        <div style={cardStyle}>
          <h2 style={cardTitle}>🧾 Företagsinformation</h2>

          <p>
            <strong>Företagsnamn:</strong>{" "}
            {loadingUser ? "Laddar..." : (activeCompany?.companyName || companyData?.name || companyData?.companyName || userData?.companyId || "Ej angivet")}
          </p>

          <p>
            <strong>Registreringsdatum:</strong>{" "}
            {loadingUser ? "Laddar..." : createdAtFormatted}
          </p>
        </div>

        <div style={cardStyle}>
          <h2 style={cardTitle}>⏱ Tidsscheman</h2>

          {loadingSchemas ? (
            <LoadingIndicator message="Laddar scheman..." />
          ) : schemas.length > 0 ? (
            <ul style={listStyle}>
              {schemas.map((schema) => (
                <li key={schema.id}>📄 {schema.name}</li>
              ))}
            </ul>
          ) : (
            <p style={emptyText}>Inga tidscheman har lagts till ännu.</p>
          )}
        </div>

        {/* ADMINISTRATÖRER - ENDAST FRÅN EMPLOYED */}
        <div style={cardStyle}>
          <h2 style={cardTitle}>👥 Administratörer</h2>

          {loadingAdmins ? (
            <LoadingIndicator message="Laddar administratörer..." />
          ) : admins.length > 0 ? (
            <ul style={listStyle}>
              {admins.map((admin) => {
                return (
                  <li key={admin.id}>
                    {admin.firstName && admin.lastName 
                      ? `${admin.firstName} ${admin.lastName}`
                      : admin.name || "Namn saknas"
                    }{" "}
                    <span style={badgeStyle}>
                      {ROLE_LABELS_SV[admin.role] || admin.role}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p style={emptyText}>Inga administratörer är registrerade.</p>
          )}
        </div>

        <div style={cardStyle}>
          <h2 style={cardTitle}>📞 Kontakt & Support</h2>

          <p>
            📧 <strong>E-post:</strong> Company@lunero.se
          </p>

          <p>
            📱 <strong>Telefon:</strong>
            <br />
            Erik Bergqvist
            <br />
           076-831 96 63
          </p>

          <p>
            📱 <strong>Telefon:</strong>
            <br />
            Daniel Florentz
            <br />
            073-907 77 87
          </p>

          <p>
            <strong>Öppettider support:</strong>
            <br />
            Mån–Fre: 10:00–17:00
            <br />
            Lör–Sön: Stängt
          </p>
        </div>

        {!timestampLoading && timestampEnabled && canAccessPage("timestamp") && (
        <div style={{ ...cardStyle, gridColumn: "1" }}>
          <h2 style={cardTitle}>🧩 Tillägg</h2>

            <div>
              <p>
                <strong>Tidstämpling:</strong>{" "}
                Aktiverad
              </p>
            </div>
          <h3 style={{ ...cardTitle, marginTop: "20px" }}>🔑 Företagskod</h3>
          <p style={{ margin: 0, fontFamily: "monospace", fontSize: "20px", overflowWrap: "anywhere" }}>
            {companyData?.companyCode || "Företagskod saknas."}
          </p>
          {companyCodeError && <p role="alert" style={{ color: "#b91c1c" }}>{companyCodeError}</p>}
        </div>
        )}
      </div>
    </div>
  );
}

export default BusinessPage;
